import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { resolvePackItem, packTotal } from '@theme/quantity-packs';
import { cartFromSections } from '@theme/cart-add-result';

/**
 * "Complete your routine": adds the page product (its selected variant) plus the
 * checked kit products in one request, then announces the change with the same
 * CartLinesUpdateEvent the product form uses, so the cart drawer opens and refreshes.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement[]} [items] - Kit product checkboxes.
 * @property {HTMLElement} summary - Selection summary text.
 * @property {HTMLElement} total - Routine total.
 * @property {HTMLButtonElement} button - Add-to-cart button.
 * @property {HTMLElement} buttonLabel - Button text.
 * @property {HTMLElement} error - Error message.
 * @property {HTMLElement} [progress] - Routine discount progress line.
 * @property {HTMLButtonElement} [selectAll] - Selects every kit product.
 *
 * @extends Component<Refs>
 */
class CompleteKitComponent extends Component {
  requiredRefs = ['summary', 'total', 'button', 'buttonLabel', 'error'];

  /** @type {AbortController | null} */
  #abort = null;

  #defaultLabel = '';
  #emptyLabel = '';

  connectedCallback() {
    super.connectedCallback();
    this.#defaultLabel = this.refs.buttonLabel.textContent ?? '';
    this.#emptyLabel = this.refs.summary.textContent ?? '';
    this.#listen();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#listen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  #listen() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('change', this.#update, { signal });
    this.refs.button.addEventListener('click', this.#addToCart, { signal });
    this.refs.selectAll?.addEventListener('click', this.#selectAll, { signal });
    // The page product's variant can change (pack selector); refresh the total once the form has it.
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#handleVariant, { signal });
    this.closest('.shopify-section')?.addEventListener('input', this.#update, { signal });
    this.closest('.shopify-section')?.addEventListener('change', this.#update, { signal });
    this.#update();
  }

  get #selected() {
    return (this.refs.items ?? []).filter((input) => input.checked);
  }

  /** The variant currently selected in this page's product form. */
  get #mainVariantId() {
    const form = this.closest('.shopify-section')?.querySelector('form[action*="/cart/add"] input[name="id"]');
    return form instanceof HTMLInputElement ? form.value : '';
  }

  get #mainQuantity() {
    const quantity = this.closest('.shopify-section')?.querySelector('form[action*="/cart/add"] [name="quantity"]');
    return quantity instanceof HTMLInputElement ? Math.max(1, Number(quantity.value) || 1) : 1;
  }

  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat ?? '{{amount}}', this.dataset.currency ?? 'USD');
  }

  /** Price of the page product's selected variant, in minor units. */
  get #mainPrice() {
    try {
      const prices = JSON.parse(this.dataset.variantPrices || '{}');
      return packTotal(this.#mainVariantId, this.#mainQuantity) ?? Number(prices[this.#mainVariantId] ?? Object.values(prices)[0] ?? 0) * this.#mainQuantity;
    } catch {
      return 0;
    }
  }

  /** Price of the page product's selected variant without pack savings, in minor units. */
  get #mainFullPrice() {
    try {
      const prices = JSON.parse(this.dataset.variantPrices || '{}');
      const { id, quantity } = resolvePackItem(this.#mainVariantId, this.#mainQuantity);
      return Number(prices[id] ?? prices[this.#mainVariantId] ?? Object.values(prices)[0] ?? 0) * quantity;
    } catch {
      return 0;
    }
  }

  /** Routine discount tiers [[minItems, percent], ...] from the block settings. */
  get #tiers() {
    try {
      return /** @type {number[][]} */ (JSON.parse(this.dataset.tiers || '[]'));
    } catch {
      return [];
    }
  }

  #selectAll = () => {
    for (const input of this.refs.items ?? []) input.checked = true;
    this.#update();
  };

  /**
   * Shows how many more items unlock the next routine tier and returns the active percent.
   * @param {number} count - Units in the routine (page product + selected items).
   */
  #renderProgress(count) {
    const { progress } = this.refs;
    const tiers = this.#tiers;
    const active = tiers.filter(([min]) => count >= min).pop();
    const next = tiers.find(([min]) => count < min);
    if (!progress || !tiers.length) return 0;

    progress.hidden = false;
    progress.toggleAttribute('data-unlocked', Boolean(active) && !next);
    const fill = (text, values) => text.replace('[count]', String(values.count)).replace('[percent]', String(values.percent));
    progress.textContent = next
      ? fill(this.dataset.progressText ?? '', { count: next[0] - count, percent: next[1] })
      : fill(this.dataset.unlockedText ?? '', { count: 0, percent: active?.[1] ?? 0 });
    return active?.[1] ?? 0;
  }

  /** @param {Event} event */
  #handleVariant = (event) => {
    const { promise } = /** @type {any} */ (event);
    if (promise) promise.then(this.#update).catch(() => {});
    else this.#update();
  };

  #update = () => {
    const selected = this.#selected;
    const { summary, total, button, buttonLabel, error } = this.refs;
    error.hidden = true;

    const count = resolvePackItem(this.#mainVariantId, this.#mainQuantity).quantity + selected.reduce((sum,input) => sum + resolvePackItem(input.value).quantity, 0);
    const percent = this.#renderProgress(count);

    if (!selected.length) {
      summary.textContent = this.#emptyLabel;
      total.textContent = '';
      button.disabled = true;
      buttonLabel.textContent = this.#defaultLabel;
      return;
    }

    const cents = selected.reduce((sum, input) => sum + Number(input.dataset.price || 0), this.#mainPrice);
    // Shopify keeps the best product discount per line: pack price or routine percent.
    // Shopify rounds the percentage discount down per unit.
    const off = (unit, quantity = 1) => (unit - Math.floor((unit * percent) / 100)) * quantity;
    const mainUnits = resolvePackItem(this.#mainVariantId, this.#mainQuantity).quantity;
    const discounted = percent
      ? Math.min(this.#mainPrice, off(this.#mainFullPrice / mainUnits, mainUnits)) + selected.reduce((sum, input) => sum + off(Number(input.dataset.price || 0)), 0)
      : cents;
    summary.textContent = `${this.dataset.productLabel} + ${selected.length} ${selected.length === 1 ? this.dataset.itemOne : this.dataset.itemOther}`;
    total.innerHTML = discounted < cents ? `<s>${this.#money(cents)}</s>${this.#money(discounted)}` : this.#money(cents);
    button.disabled = false;
    buttonLabel.textContent = `${this.#defaultLabel} · ${count} ${count === 1 ? this.dataset.itemOne : this.dataset.itemOther}`;
  };

  #addToCart = async () => {
    const { button, error } = this.refs;
    const mainId = this.#mainVariantId;
    const items = [
      ...(mainId ? [resolvePackItem(mainId, this.#mainQuantity)] : []),
      ...this.#selected.map((input) => resolvePackItem(input.value)),
    ];
    if (!items.length) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean))];

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', { body: JSON.stringify({ items, sections: sectionIds.join(',') }) }),
      }).then((res) => res.json());

      if (response.status) throw new Error(response.description || response.message || this.dataset.errorAdd);

      const cart = cartFromSections(response.sections) ?? await fetch(`${Theme.routes.cart_url}.js`).then((res) => {
        if (!res.ok) throw new Error(this.dataset.errorRefresh);
        return res.json();
      });
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'complete-kit',
          itemCount: items.length,
          sections: response.sections,
          didError: false,
        },
      });

      for (const input of this.#selected) input.checked = false;
      this.#update();
    } catch (err) {
      deferred.reject(err);
      error.textContent = err instanceof Error ? err.message : this.dataset.errorGeneric;
      error.hidden = false;
      button.disabled = false;
    } finally {
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('complete-kit-component')) {
  customElements.define('complete-kit-component', CompleteKitComponent);
}
